import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckIcon, 
  ArrowRightIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { ProductCarousel } from '../../components/marketing/ProductCarousel';
import { InteractiveProductsSection } from '../../components/marketing/InteractiveProductsSection';

export const HomePage = () => {
  const features = [
    {
      icon: <ComputerDesktopIcon className="h-8 w-8" />,
      title: "DICOMFiles",
      description: "Gestión completa de archivos DICOM con visualización, conversión y organización avanzada de estudios médicos."
    },
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: "DICOMCapture",
      description: "Captura y procesamiento de imágenes DICOM desde equipos médicos con validación y control de calidad."
    },
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      title: "Análisis Avanzado",
      description: "Herramientas de análisis y procesamiento de imágenes médicas con algoritmos especializados."
    },
    {
      icon: <CogIcon className="h-8 w-8" />,
      title: "Integración Completa",
      description: "Compatibilidad total con equipos médicos y sistemas PACS para flujos de trabajo optimizados."
    }
  ];

  const benefits = [
    "Visualización avanzada de imágenes DICOM",
    "Procesamiento y análisis de estudios médicos",
    "Integración con equipos y sistemas PACS",
    "Soporte técnico especializado en DICOM",
    "Implementación rápida y personalizada",
    "Cumplimiento con estándares médicos"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Soluciones DICOM
              <span className="text-blue-600 block">Profesionales</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Software especializado para el manejo y procesamiento de imágenes médicas DICOM. 
              Herramientas profesionales para clínicas, hospitales y centros de diagnóstico.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Products Section */}
      <InteractiveProductsSection />

      {/* Product Carousel Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explora Nuestros Productos
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Descubre más detalles sobre nuestras soluciones especializadas
            </p>
          </div>
          <ProductCarousel />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              ¿Por qué elegir DICOMITY?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Nuestros productos están diseñados específicamente para profesionales médicos 
              que necesitan herramientas avanzadas para el manejo de imágenes DICOM.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-200">
                <div className="text-blue-600 mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Optimiza tu flujo de trabajo DICOM
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Con DICOMFiles y DICOMCapture, tendrás herramientas profesionales para 
                el manejo eficiente de imágenes médicas, mejorando la productividad y calidad diagnóstica.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center">
                    <CheckIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">100+</div>
                <div className="text-gray-600 mb-6">Centros médicos</div>
                <div className="text-4xl font-bold text-green-600 mb-2">50K+</div>
                <div className="text-gray-600 mb-6">Estudios procesados</div>
                <div className="text-4xl font-bold text-purple-600 mb-2">24/7</div>
                <div className="text-gray-600">Soporte técnico</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para optimizar tu flujo de trabajo DICOM?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Comienza hoy mismo y descubre cómo DICOMFiles y DICOMCapture pueden 
            transformar el manejo de imágenes médicas en tu centro.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="border border-white text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              Solicitar Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
